import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { v4 as uuid } from 'uuid';
import { Store } from '@ngrx/store';
import { LoadingModule } from '@components/loading';
import { CsIconModule } from '@components/icon';
import { EventLoggerService } from '@services/event-logger-service';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { RegimenService } from '@services/regimen-service';
import { ExperimentService } from '@services/experiment';
import { fromActions } from '@store/actions';
import { AppConfig } from '../../app.config';
import { AllocatedRegimen } from '../../app-constant-types';
import { DIET_BANNER_CONCERN_TYPE } from '../../app.constants';
import { REGIMEN_QUERY_PARAMS } from '../../user/user-view/user-regimen/regimen.constants';

@Component({
  selector: 'app-diet-plan-v2',
  templateUrl: './diet-plan-v2.html',
  imports: [CommonModule, LoadingModule, CsIconModule],
})
export class DietPlanV2Component implements OnInit {
  @Input() user: any;

  loading: boolean = true;
  // Kept separate from `loading`: data resolves first, but the large content PNGs load afterwards.
  // We keep the body hidden behind the loader until they settle so the buy section doesn't flash in early.
  imagesReady: boolean = false;
  private settledContentImages: number = 0;
  userFirstName: string;

  faceRegimen: AllocatedRegimen;
  hairRegimen: AllocatedRegimen;
  availableTabs: Array<string> = [];
  activeTab: string;

  consultationService: any;
  basicPrice: string;
  proPlanId: string;
  proPlanPrice: number;
  selectedPlan: string;

  readonly dietPlans: any = this.appConfig.Shared.Notice.dietPlans;
  readonly Class: any = this.appConfig.Shared.Regimen.Class;

  constructor(
    private readonly conn: ConnectionService,
    private readonly broadcast: BroadcastService,
    protected readonly appConfig: AppConfig,
    private readonly store: Store,
    private readonly eventLogger: EventLoggerService,
    private readonly route: ActivatedRoute,
    private readonly regimenService: RegimenService,
    private readonly experimentService: ExperimentService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.user = this.user || this.conn.getActingUser();
    this.userFirstName = this.user?.get('PatientName')?.split(' ')[0];
    this.selectedPlan = this.dietPlans.BASIC;
    // Load regimens (tabs + branch) and pricing independently so a failure in one never blocks the other.
    try {
      await Promise.all([this.loadRegimens(), this.loadPricing()]);
      // Log the page open with the tab the user landed on (FACE/HAIR) and branch bucket.
      this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'diet-plan-v2', tab: this.activeTab, type: this.branch }));
    } finally {
      this.loading = false;
      this.watchContentImages();
    }
  }

  // (Re)start watching for the active branch's images to settle, keeping the body hidden behind the
  // loader until the (load)/(error) events fire. Called on initial load and on tab switch.
  private watchContentImages(): void {
    this.imagesReady = false;
    this.settledContentImages = 0;
    // Nothing to wait for — reveal immediately.
    if (this.expectedContentImages === 0) this.markImagesReady();
  }

  // Only the active branch's images are mounted (top + bottom). 0 if the active branch has no regimen.
  private get expectedContentImages(): number {
    return this.contentImageBranch ? 2 : 0;
  }

  // Fired on every content image's load/error; reveals the page once all of them have settled.
  onContentImageSettled(): void {
    this.settledContentImages += 1;
    if (this.settledContentImages >= this.expectedContentImages) this.markImagesReady();
  }

  private markImagesReady(): void {
    this.imagesReady = true;
  }

  private async loadRegimens(): Promise<void> {
    try {
      // getRegimens reuses the store when regimens were already fetched (e.g. from the regimen page).
      const regimens: Array<AllocatedRegimen> = (await this.regimenService.getRegimens()) || [];
      // Mirror the home page's dietBannerRegimen priority: prefer a delivered regimen, fall back to any.
      this.faceRegimen = regimens.find((r: AllocatedRegimen): boolean => r?.class === this.Class.FACE && !!r?.deliveredDate)
        || regimens.find((r: AllocatedRegimen): boolean => r?.class === this.Class.FACE);
      this.hairRegimen = regimens.find((r: AllocatedRegimen): boolean => r?.class === this.Class.HAIR && !!r?.deliveredDate)
        || regimens.find((r: AllocatedRegimen): boolean => r?.class === this.Class.HAIR);
      if (this.faceRegimen) this.availableTabs.push(this.Class.FACE);
      if (this.hairRegimen) this.availableTabs.push(this.Class.HAIR);
      this.activeTab = this.resolveInitialTab();
    } catch {
      this.activeTab = this.Class.FACE;
    }
  }

  // Initial tab: honour the `class` query param (set by the diet banner / instruction page) when the
  // matching regimen exists; otherwise default to FACE when available, else HAIR (availableTabs order).
  private resolveInitialTab(): string {
    const requested: string = this.route.snapshot.queryParams[REGIMEN_QUERY_PARAMS.CLASS];
    if (requested === this.Class.HAIR && this.hairRegimen) return this.Class.HAIR;
    if (requested === this.Class.FACE && this.faceRegimen) return this.Class.FACE;
    return this.availableTabs[0] || this.Class.FACE;
  }

  // BASIC price from the diet service; PRO pricing from the diet_1500 experiment (already loaded by
  // ExperimentService when the page mounted), so no extra experiment fetch here.
  private async loadPricing(): Promise<void> {
    try {
      this.consultationService = await this.conn.fetchDietService();
      this.basicPrice = this.consultationService?.get('amount');
    } catch {
      // ignore — BASIC price stays unset
    }
    const proPricing = this.experimentService.getDietPlanProPricing();
    if (proPricing) {
      this.proPlanId = proPricing.proPlanId;
      this.proPlanPrice = proPricing.proPlanPrice;
    }
  }

  // Content images are exported per language; only Hindi has localised assets, everything else uses English.
  get isHindi(): boolean {
    return this.user?.get('languagePreference') === this.appConfig.Shared.Languages.HI;
  }

  get isHair(): boolean {
    return this.activeTab === this.Class.HAIR;
  }

  get isAcne(): boolean {
    const treatmentConcerns: Array<string> = this.faceRegimen?.displayConcern?.treatmentConcerns ?? [];
    return treatmentConcerns.includes(this.appConfig.Shared.String.ACNE_OR_PIMPLES);
  }

  // 'hair' | 'acne' | 'non_acne' — same buckets as the diet banner (DIET_BANNER_CONCERN_TYPE); used for analytics.
  get branch(): string {
    if (this.isHair) return DIET_BANNER_CONCERN_TYPE.HAIR;
    return this.isAcne ? DIET_BANNER_CONCERN_TYPE.ACNE : DIET_BANNER_CONCERN_TYPE.NON_ACNE;
  }

  get isNonAcne(): boolean {
    return !this.isHair && !this.isAcne;
  }

  // Base URL for the diet plan content images (served from CDN). Filenames follow
  // `<branch>-<top|bottom>[-hi].png`, e.g. hair-top-hi.png / non-acne-bottom.png.
  private readonly DIET_IMAGE_BASE: string = 'https://cdn.cureskin.com/app/img';

  // Active branch for the current tab: 'hair' | 'acne' | 'non-acne', or null when the tab has no regimen.
  private get contentImageBranch(): string | null {
    if (this.isHair) return this.hairRegimen ? 'hair' : null;
    if (!this.faceRegimen) return null;
    return this.isAcne ? 'acne' : 'non-acne';
  }

  // Full URLs for the top/bottom content images of the active branch + language; null when none applies.
  get topImageUrl(): string | null {
    return this.contentImageUrl('top');
  }

  get bottomImageUrl(): string | null {
    return this.contentImageUrl('bottom');
  }

  private contentImageUrl(position: 'top' | 'bottom'): string | null {
    const branch: string | null = this.contentImageBranch;
    if (!branch) return null;
    const langSuffix: string = this.isHindi ? '-hi' : '';
    return `${this.DIET_IMAGE_BASE}/${branch}-${position}${langSuffix}.png`;
  }

  // Static images on the dietician card (not branch/language specific).
  get dieticianImageUrl(): string {
    return `${this.DIET_IMAGE_BASE}/diet-dr.png`;
  }

  get createdByIconUrl(): string {
    return `${this.DIET_IMAGE_BASE}/yellow-teal.svg`;
  }

  selectTab(tab: string): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'diet-plan-tab', tab, type: this.branch }));
    // The new branch's image isn't loaded yet — hold the body behind the loader until it settles.
    this.watchContentImages();
  }

  selectPlan(plan: string): void {
    if (this.selectedPlan === plan) return;
    this.selectedPlan = plan;
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'select-diet-plan', concern_type: this.branch, plan }));
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  buy(): void {
    const serviceId: string = this.selectedPlan === this.dietPlans.PRO ? this.proPlanId : this.consultationService.id;
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'buy-consultation', concern_type: this.branch, plan: this.selectedPlan }));
    const payload: any = {
      userObjectId: this.user.id,
      paymentType: this.appConfig.Shared.Order.PaymentType.NOT_SELECTED,
      type: this.appConfig.Shared.Order.Type.CONSULTATION,
      uniqueId: uuid(),
      services: serviceId,
    };
    this.store.dispatch(fromActions.CheckoutOrderBegin({ payload, replaceUrl: false }));
  }
}
